import React from "react";
import { useWatch } from "react-hook-form";

import FormInputCurrency from "components/Form/Input/Currency";
import FormInputNumber from "components/Form/Input/Number";
import FormInputRadio from "components/Form/Input/Radio";
import FormInputText from "components/Form/Input/Text";
import FormTitle from "components/Form/Title";
import FormErrors from "pages/ApplicationForm/components/FormErrors";
import { checkTruthyValue } from "utils/validation";
import { StepProps } from "../../types";
import { LABELS } from "./labels";
import useApplicationStore from "store/Application";

const STEP_KEY = "loan";

const LoanForm: React.FC<StepProps> = ({
  control,
  register,
  errors,
  setValue,
}) => {
  const loanDetail = useWatch({ name: STEP_KEY, control: control });
  const { avmValuation } = useApplicationStore();

  return (
    <div className="flex flex-col space-y-4">
      <FormTitle left border title="Loan" mb={false} />

      <FormErrors formErrors={errors?.[STEP_KEY]} />

      <FormInputText
        id={`${STEP_KEY}.purposeOfLoan`}
        label={LABELS.purposeOfLoan}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.purposeOfLoan?.message}
      />

      <FormInputNumber
        id={`${STEP_KEY}.loanTermMonths`}
        label={LABELS.loanTermMonths}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.loanTermMonths?.message}
      />

      <FormInputCurrency
        id={`${STEP_KEY}.loanAmount`}
        label={LABELS.loanAmount}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.loanAmount?.message}
        setValue={setValue}
      />

      <FormInputCurrency
        id={`${STEP_KEY}.firstChargeMortgageValue`}
        label={LABELS.firstChargeMortgageValue}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.firstChargeMortgageValue?.message}
        setValue={setValue}
      />

      <FormInputCurrency
        id={`${STEP_KEY}.propertyPurchasePrice`}
        label={LABELS.propertyPurchasePrice}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.propertyPurchasePrice?.message}
        setValue={setValue}
      />

      <FormInputCurrency
        id={`${STEP_KEY}.estimatedPropertyValue`}
        label={LABELS.estimatedPropertyValue}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.estimatedPropertyValue?.message}
        setValue={setValue}
        helperText={
          avmValuation
            ? `AVM valuation is £${avmValuation}, if you change this we might request a full valuation.`
            : ""
        }
      />

      <FormInputText
        id={`${STEP_KEY}.firstChargeLender`}
        label={LABELS.firstChargeLender}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.firstChargeLender?.message}
      />

      <FormInputNumber
        id={`${STEP_KEY}.mortgageAgeYears`}
        label={LABELS.mortgageAgeYears}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.mortgageAgeYears?.message}
      />

      <FormInputNumber
        id={`${STEP_KEY}.mortgageAgeMonths`}
        label={LABELS.mortgageAgeMonths}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.mortgageAgeMonths?.message}
      />

      <FormInputNumber
        id={`${STEP_KEY}.remainingTermOfFirstChargeLoanYears`}
        label={LABELS.remainingTermOfFirstChargeLoanYears}
        register={register}
        requiredField={false}
        error={errors?.[STEP_KEY]?.remainingTermOfFirstChargeLoanYears?.message}
      />

      <FormInputNumber
        id={`${STEP_KEY}.remainingTermOfFirstChargeLoanMonths`}
        label={LABELS.remainingTermOfFirstChargeLoanMonths}
        register={register}
        requiredField={false}
        error={
          errors?.[STEP_KEY]?.remainingTermOfFirstChargeLoanMonths?.message
        }
      />

      <FormInputRadio
        id={`${STEP_KEY}.isInterestRateFixedForAnInitialPeriod`}
        label={LABELS.isInterestRateFixedForAnInitialPeriod}
        register={register}
        requiredField={false}
        initValue={loanDetail?.isInterestRateFixedForAnInitialPeriod}
        error={
          errors?.[STEP_KEY]?.isInterestRateFixedForAnInitialPeriod?.message
        }
      />

      {checkTruthyValue(loanDetail?.isInterestRateFixedForAnInitialPeriod) && (
        <FormInputNumber
          id={`${STEP_KEY}.remainingTermOfInitialInterestRatePeriodMonths`}
          label={LABELS.remainingTermOfInitialInterestRatePeriodMonths}
          register={register}
          requiredField={false}
          error={
            errors?.[STEP_KEY]?.remainingTermOfInitialInterestRatePeriodMonths
              ?.message
          }
        />
      )}
    </div>
  );
};

export default LoanForm;
